/* @HEADER
 * @version 1.1.1294 | 2026-10-30
 * @purpose The authenticated app's boot seed: ensure the default board group, board and the system spaces (All tasks / All projects / Org tasks), reconcile the active project's Space tabs from its registry (ZVX-DEV-2721), heal orphaned board→Space references (ZVX-DEV-3274), seed the personal plan once (ZVX-DEV-6242), refresh the groups, publish the dev/e2e __zvenixDbReady seam (ZVX-DEV-1501/4399) and fire onReady (ZVX-DEV-2745). Moved out of App.tsx unchanged (ZVX-DEV-5944, campaign 3) as a hook AuthenticatedApp calls once on mount.
 * @sidecar useProjectBootSeed.ts.header.md
 * @layer app | @hex adapter | @ctx workspace
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-5944, ZVX-DEV-3274, ZVX-DEV-1501, ZVX-DEV-4399, ZVX-DEV-2745, ZVX-DEV-2735

import { useEffect } from 'react';
import {
  ensureDefaultBoardGroup,
  ensureDefaultBoard,
  ensureAllTasksSpace,
  ensureAllTasksDefaultBoard,
  ensureAllProjectsSpace,
  ensureAllProjectsDefaultBoard,
  ensureOrgTasksSpace,
  ensureOrgTasksDefaultBoard,
  syncActiveProjectSpaces,
  healOrphanBoardReferencesForActiveProject,
} from '../lib/db';
// ZVX-DEV-6829 -- the pure summariser behind the boot's marker-repair line.
import { summariseSystemBoardMarkerRepair } from '@modules/board/public-api.mjs';
import { isBenignDbClosingError } from '../lib/db/dbErrors';
// ZVX-DEV-6242 (D2-b): the personal plan is seeded once, after the built-in
// spaces exist and before the groups are refreshed, so the first render shows it.
import { ensurePersonalOrgSeedForActiveProject } from '../lib/db/personalOrgSeedBoot';
import { isDevDiagnosticSurfaceEnabled, isWave6DebugEnabled } from '../shared/featureFlags';

/**
 * ZVX-DEV-5944 -- AuthenticatedApp's mount-time seed effect, moved here as
 * text: same body, same empty dependency list. It reads two things from the
 * component -- the board groups' `refresh` and the host's `onReady` -- and
 * takes exactly those. Called once, where the effect used to stand.
 */
export function useProjectBootSeed({
  refreshGroups,
  onReady,
}: {
  refreshGroups: () => void;
  onReady?: () => void;
}): void {
  useEffect(() => {
    Promise.all([
      ensureDefaultBoardGroup(),
      ensureDefaultBoard(),
      ensureAllTasksSpace(),
      ensureAllTasksDefaultBoard(),
      ensureAllProjectsSpace(),
      ensureAllProjectsDefaultBoard(),
      ensureOrgTasksSpace(),
      ensureOrgTasksDefaultBoard(),
    ])
      // ZVX-DEV-6829 -- say HOW MANY service boards got their marker, not that
      // the seed ran. The operator's instruction is that no malformed board may
      // be left, and «сколько» is the first question he asks of it.
      //
      // The sentence names its own limit on purpose: a boot visits ONE project
      // database, which holds at most these four service boards. «N of 4 in this
      // project» is what was measured; anything shorter would be read as «all
      // your boards», which this run cannot know.
      .then((results) => {
        const summary = summariseSystemBoardMarkerRepair([
          results[1],
          results[3],
          results[5],
          results[7],
        ] as string[]);
        // Silent on a healthy database: `changed` is false when nothing was
        // written, and a line every boot would train the reader to skip it.
        if (summary.changed) {
          console.info(
            `[system-board-markers] repaired ${summary.repaired} of ${summary.visited} service board(s) in this project` +
              (summary.created > 0 ? `, seeded ${summary.created}` : ''),
          );
        }
        return results;
      })
      // ZVX-DEV-2721: seed/reconcile the active project's Space tabs from its
      // registry-discovered serverSpaces (ADR-0092 D7) AFTER the default
      // system spaces exist, so a freshly-discovered project (switched into
      // via ProjectSwitcher's window.location.reload()) renders its real
      // Space tabs before any board content has p2p-loaded. Best-effort —
      // a failure here must not block the default-seed boot path.
      // ZVX-3805 -- the boot reconcile announces even when no Space differs: a list drawn before the boards arrived reads again on it. Only the later, periodic cycles over unchanged Spaces are silent.
      .then(() => syncActiveProjectSpaces({ announceAnyway: true }).catch(() => {}))
      // ZVX-DEV-3274: repair databases that ALREADY carry orphaned board→Space
      // references (every pre-fix syncActiveProjectSpaces run deleted Space
      // rows without re-hanging their boards). Runs AFTER the reconcile above
      // so the successor Spaces it re-hangs onto already exist locally.
      // Idempotent, put-only, db-name-guarded, never throws — free on a
      // healthy database.
      .then(() => healOrphanBoardReferencesForActiveProject().catch(() => 0))
      .then(() =>
        ensurePersonalOrgSeedForActiveProject().catch((err) => {
          if (!isBenignDbClosingError(err)) console.error('[board] personal seed failed:', err);
        }),
      )
      .then(() => {
        refreshGroups();
        // ZVX-DEV-1501 (Wave 6 final-mile, Bug F): mark the host IDB as ready
        // so the Playwright Wave 6 spec's seed helper can wait for a real
        // signal instead of polling-and-hoping for the v18 stores. The flag
        // lives on `window` (test-only seam) and is intentionally set AFTER
        // `ensureDefaultBoardGroup` resolves — by that point getDb() has run
        // the upgrade to DB_VERSION=18, so the boardGroups/boards/tasks stores
        // the seed needs are guaranteed to exist. A no-op in production —
        // nothing reads this flag in the shipped UI.
        try {
          // ZVX-DEV-4399 -- published only in dev/e2e. It is a test seam
          // (tests/e2e/scenarios/hpmor-mobile.ts waits on it) and the comment
          // above already said nothing in the shipped UI reads it; a global
          // that exists in production for no reader is a promise to nobody,
          // kept forever. The e2e walkthrough serves the app with `pnpm dev`,
          // so this stays true exactly where the spec needs it.
          if (isDevDiagnosticSurfaceEnabled()) {
            (window as unknown as { __zvenixDbReady?: boolean }).__zvenixDbReady = true;
          }
          // ZVX-DEV-1520 (Wave 6 diagnostic instrumentation): log the moment
          // the flag flips so the spec can correlate seed-completion timing
          // against AuthenticatedApp mount and presence WS connect.
          if (isWave6DebugEnabled()) {
            // eslint-disable-next-line no-console
            console.log('[wave6][App] __zvenixDbReady=true', { t: Date.now() });
          }
        } catch {
          /* SSR / sandbox — non-critical */
        }
        // ZVX-DEV-2745: this is the real "first project data is ready"
        // signal for the in-place project-switch overlay — fires after the
        // same seed/reconcile chain the Wave 6 __zvenixDbReady flag above
        // already gates on, so the overlay never clears before the new
        // project's default spaces/boards exist.
        onReady?.();
      })
      // ZVX-DEV-2735: a reload mid-seed races IDB teardown with a benign
      // InvalidStateError ("connection is closing/closed") — not a real failure.
      .catch((err) => {
        if (!isBenignDbClosingError(err)) console.error('[board] seed failed:', err);
        // ZVX-DEV-2745: clear the overlay even on a seed failure — a stuck
        // overlay is worse than showing a possibly-incomplete screen, and
        // the host also carries its own hard safety timeout.
        onReady?.();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
